/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AfterViewInit, Directive, ElementRef, HostListener, Input } from '@angular/core';
import { LogService, ValidationService } from '../../services';

@Directive({
    selector: '[appInputValidator]'
})
/**
 * @name InputValidatorDirective
 * @description
 * ## Validation rules:
 *
 */
export class InputValidatorDirective implements AfterViewInit {
    @Input() set maximumLength(length) {
        this._maximumLength = length;
    }

    /**
     * Store navigation keys.
     * @type {string[]}
     * @private
     */
    private navigationKeys: string[] = [
        'ArrowDown',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'Backspace',
        'Clear',
        'Copy',
        'Delete',
        'End',
        'Enter',
        'Escape',
        'Home',
        'Paste',
        'Tab'
    ];

    /**
     * Store the html element.
     * @private
     */
    private inputElement: any;

    /**
     * Store the input type e.g. number, text.
     * @private
     */
    private inputType: string;

    /**
     * Store the name property of the input.
     * @private
     */
    private inputName: string;

    /**
     * Store max character length of input.
     * @private
     */
    private _maximumLength: number;

    constructor(
        private el: ElementRef,
        private logService: LogService
    ) {

    }

    get maximumLength() {
        return this._maximumLength;
    }

    public ngAfterViewInit() {
        this.inputElement = this.el.nativeElement;
        this.inputName = this.inputElement.name;
        this.inputType = this.inputElement['type'];
        switch (this.inputName) {
            case 'port':
                this.setRestrictionsByType('port');
                break;
            case 'time_to_live':
                this.setRestrictionsByType('time_to_live');
                break;
            default:
                this.logService.debug('InputValidatorDirective: afterViewInit', 'Directive did not match name attribute');
        }
    }

    /**
     * Set min/max/length restrictions on field based on type.
     * @param {string} type
     * @private
     */
    private setRestrictionsByType(type: string): void {
        this.maximumLength = 5;
        switch (type) {
            case 'port':
                this.inputElement['min'] = 1;
                this.inputElement['max'] = 65535;
                break;
            case 'time_to_live':
                this.inputElement['min'] = 0;
                this.inputElement['max'] = 86400;
                break;
        }
    }

    /**
     * Clipboard paste
     * @param {ClipboardEvent} $event
     */
    @HostListener('paste', ['$event'])
    onPaste($event: ClipboardEvent): void {
        const pastedData: string = $event.clipboardData.getData('Text');
        const regex: RegExp = this.getRegexExpForInputName(this.inputName);
        const matches: RegExpMatchArray = pastedData.match(regex);

        if (!matches) {
            $event.preventDefault();
        }
    }

    /**
     * Key down
     * @param {KeyboardEvent} event
     */
    @HostListener('keydown', ['$event'])
    public onKeyDown(event: KeyboardEvent): void {
        const doesntIncludeSpecialKeys = !this.navigationKeys.includes(event.key) && !ValidationService.actionKeyCombinations(event);

        if (this.inputName === 'domain' || this.inputName === 'hostname' || this.inputName === 'address') {
            if (this.inputContainsInvalidCharacter(event.key, this.inputName)) {
                event.preventDefault();
            }
        }

        if (this.inputName === 'ip_address') {
            if (this.inputContainsInvalidCharacter(event.key, this.inputName) && doesntIncludeSpecialKeys) {
                event.preventDefault();
            }
        }

        if (this.inputName === 'port' || this.inputType === 'number') {
            const inputVal = (event.target as HTMLInputElement).value;
            const selection = window.getSelection().toString();
            const inputInvalid = (inputVal.length >= this.maximumLength && selection.length === 0);
            if ((/[^0-9]+/g.test(event.key) || inputInvalid) && doesntIncludeSpecialKeys) {
                event.preventDefault();
            }
        }
    }

    /**
     * Check if input contains an invalid character.
     * @param {string} text
     * @param {string} type
     * @returns {boolean}
     * @private
     */
    private inputContainsInvalidCharacter(text: string, type: string): boolean {
        const regEx = this.getRegexExpForInputName(type);

        return regEx.test(text);
    }

    /**
     * Get the regex expression to evaluate input text based on the name of the input.
     * @param {string} type
     * @returns {any}
     * @private
     */
    private getRegexExpForInputName(type: string): any {
        let regex: RegExp;
        switch (type) {
            case 'domain':
                regex = new RegExp(/[^a-zA-Z0-9._-]/);
                break;
            case 'hostname':
                regex = new RegExp(/[^0-9A-Za-z\-]/);
                break;
            case 'ip_address':
                regex = new RegExp(/[^0-9.:]/);
                break;
            case 'address':
                regex = new RegExp(/[^a-zA-Z0-9.:_-]/);
                break;
        }

        return regex;
    }
}
